import type { BillingStartChoice, InvoiceScheduleChoice, OrderForm } from '../types'
import { formatDisplayDate } from '../components/ui/date-logic'
import { lineNetPrice, linePricing } from './order-form-line'

/**
 * The structured billing terms on an order form (Rod, 2026-10-16): when
 * billing starts, the invoice schedule and the payment terms. The editor,
 * the DTO and the Stripe setup all read them through these helpers. Older
 * forms kept these as free text; the readers below map that text to a choice
 * and never change the stored text, so old documents read the same.
 */

export const BILLING_START_OPTIONS: Array<{ key: BillingStartChoice; label: string }> = [
  { key: 'at_signature', label: 'At Signature' },
  { key: 'contract_start', label: 'Contract Start Date' },
  { key: 'custom', label: 'Custom Date' },
]

export const INVOICE_SCHEDULE_OPTIONS: Array<{ key: InvoiceScheduleChoice; label: string }> = [
  { key: 'upfront', label: 'Upfront for the Full Term' },
  { key: 'annually', label: 'Annually' },
]

export const PAYMENT_TERMS_OPTIONS = ['Due Upon Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60'] as const

const BILLING_START_KEYS: BillingStartChoice[] = ['at_signature', 'contract_start', 'custom']
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** "At Signature", "Contract Start Date", or blank text: the choice the old free text meant. Unknown text reads as the default, At Signature. */
export function billingStartChoiceFromText(text: string): BillingStartChoice {
  const value = text.trim().toLowerCase()
  if (!value) return 'at_signature'
  if (/sign|execut/.test(value)) return 'at_signature'
  if (/contract start|term start|start date|effective/.test(value)) return 'contract_start'
  return 'at_signature'
}

/** "Upfront, covering all 3 years" and "Full 2-year term at signature" read as Upfront; anything else as Annually. */
export function invoiceScheduleChoiceFromText(text: string): InvoiceScheduleChoice {
  return /up[\s-]?front|in full|full .*term|all \d+ years|entire term|whole term/i.test(text) ? 'upfront' : 'annually'
}

export function billingStartChoice(form: Pick<OrderForm, 'billingStart' | 'billingStartChoice'>): BillingStartChoice {
  return form.billingStartChoice && BILLING_START_KEYS.includes(form.billingStartChoice) ? form.billingStartChoice : billingStartChoiceFromText(form.billingStart || '')
}

export function invoiceScheduleChoice(form: Pick<OrderForm, 'invoiceSchedule' | 'invoiceScheduleChoice'>): InvoiceScheduleChoice {
  return form.invoiceScheduleChoice === 'upfront' || form.invoiceScheduleChoice === 'annually' ? form.invoiceScheduleChoice : invoiceScheduleChoiceFromText(form.invoiceSchedule || '')
}

/** The Billing Start text for the document: the choice label, or the custom date as "Nov 1, 2026". */
export function billingStartText(choice: BillingStartChoice, date: string | null | undefined): string {
  if (choice === 'custom') return date && ISO_DATE.test(date) ? formatDisplayDate(date) : 'Custom Date'
  return BILLING_START_OPTIONS.find((option) => option.key === choice)!.label
}

export function invoiceScheduleText(choice: InvoiceScheduleChoice): string {
  return INVOICE_SCHEDULE_OPTIONS.find((option) => option.key === choice)!.label
}

/** Days the customer has to pay: "Due Upon Receipt" is 0, "Net 30" is 30. Null when the text names no number of days. */
export function paymentTermsDays(text: string): number | null {
  const value = text.trim()
  if (!value || /upon receipt|on receipt|due immediately/i.test(value)) return 0
  const net = value.match(/net\s*(\d{1,3})/i) || value.match(/(\d{1,3})\s*days/i)
  return net ? Number(net[1]) : null
}

/** A Stripe Dashboard link for an object, in the test or live dashboard. */
export function stripeDashboardUrl(mode: 'test' | 'live', kind: 'customers' | 'subscriptions' | 'subscription_schedules' | 'invoices', id: string): string {
  return `https://dashboard.stripe.com/${mode === 'test' ? 'test/' : ''}${kind}/${encodeURIComponent(id)}`
}

/**
 * The License Overage rate Stripe bills per extra License each month (Rod,
 * 2026-10-27). Every form with a priced license line gets the overage
 * subscription. The rate is the form's Overage Rate when it is set.
 * Otherwise it is the full per-license price: the license line's net unit
 * price plus the net unit prices of the per-unit add-on lines. For licenses
 * at $23 with two $3 per-license add-ons, that is $29. Null when there is no
 * per-unit license line with a price and no Overage Rate.
 */
export function stripeOverageRate(form: Pick<OrderForm, 'lines' | 'overageRate'>): { amount: number; source: 'form' | 'default' } | null {
  // A flat license line has no per-license price, so it gives no default rate.
  const license = form.lines.find((line) => line.category === 'license' && linePricing(line) === 'per_unit' && lineNetPrice(line) !== null)
  if (typeof form.overageRate === 'number' && Number.isFinite(form.overageRate) && form.overageRate > 0) return { amount: form.overageRate, source: 'form' }
  if (!license) return null
  const addOns = form.lines
    .filter((line) => line.category === 'add_on' && linePricing(line) === 'per_unit')
    .reduce((sum, line) => sum + (lineNetPrice(line) ?? 0), 0)
  const amount = Math.round(((lineNetPrice(license) ?? 0) + addOns) * 1e6) / 1e6
  return amount > 0 ? { amount, source: 'default' } : null
}

/**
 * Stripe setups made before 2026-10-28 billed overage only when the form had
 * an Overage Rate. Those forms keep that rule, so a retry never adds an
 * overage subscription to a contract that was billed without one.
 */
export const STRIPE_DEFAULT_OVERAGE_SINCE = '2026-10-28T00:00:00.000Z'

/** True when the Stripe setup for this form needs the overage subscription. */
export function stripeOverageExpected(form: Pick<OrderForm, 'lines' | 'overageRate'>, state: { createdAt: string | null; meterId?: string | null } | null | undefined): boolean {
  const rate = stripeOverageRate(form)
  if (!rate) return false
  if (rate.source === 'form') return true
  const olderSetup = Boolean(state?.meterId) || Boolean(state?.createdAt && state.createdAt < STRIPE_DEFAULT_OVERAGE_SINCE)
  return !olderSetup
}
